import Cookies from 'js-cookie';
import { AUTH_TOKEN_KEY } from '@/lib/constants';
import { clientLogger } from '@/lib/client-logger';
import client from '@/framework/client';
import { API_ENDPOINTS } from '@/framework/client/api-endpoints';
import type { CartSyncPayload, ServerCartLine } from './cart-sync.types';

const baseUrl = () =>
  (process.env.NEXT_PUBLIC_REST_API_ENDPOINT ?? '').replace(/\/$/, '');

export async function postCartSync(payload: CartSyncPayload): Promise<void> {
  if (!payload.items?.length) return;
  await client.cart.sync(payload);
}

export async function getServerCart(): Promise<{ items: ServerCartLine[] }> {
  const raw = (await client.cart.get()) as
    | { items?: ServerCartLine[] }
    | ServerCartLine[]
    | null
    | undefined;
  if (Array.isArray(raw)) {
    return { items: raw };
  }
  return { items: raw?.items ?? [] };
}

export async function deleteServerCart(): Promise<void> {
  await client.cart.delete();
}

/**
 * DELETE /cart after checkout: must never break the purchase UX. Retries + swallow final failure.
 */
export async function deleteServerCartBestEffort(): Promise<void> {
  const maxAttempts = 3;
  let lastErr: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await new Promise((r) =>
        setTimeout(r, attempt === 1 ? 120 : 350),
      );
    }
    try {
      await deleteServerCart();
      return;
    } catch (err) {
      lastErr = err;
    }
  }
  clientLogger.warn('deleteServerCartBestEffort: all attempts failed', {
    error: lastErr instanceof Error ? lastErr.message : String(lastErr),
  });
}

/**
 * Best-effort flush when the document may unload (Bearer token — use fetch+keepalive, not sendBeacon).
 */
export function postCartSyncKeepalive(payload: CartSyncPayload): void {
  const token = Cookies.get(AUTH_TOKEN_KEY);
  if (!token || !payload.items?.length) return;
  const url = `${baseUrl()}${API_ENDPOINTS.CART}/sync`;
  try {
    void fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    /* silent */
  }
}
